'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'

// Types
import type { PricingPlansProps } from '@/types/components/organisms/pricing'

// Components
const PricingPeriodToggleMolecule = dynamic(() => import('@/components/molecules/pricing/period-toggle'))
const PricingEmptyMolecule = dynamic(() => import('@/components/molecules/pricing/empty'))
const PricingComparisonOrganism = dynamic(() => import('@/components/organisms/pricing/comparison'))

// Billing period switch, then one table with the prices, solutions and limits of its plans, and a demo link for anyone still unsure
const PricingPlansOrganism = (props: PricingPlansProps) => {
	// Props
	const { lng, locale, packages } = props

	// Variables
	const { period, help, empty } = locale.content.pricingPage
	const plans = packages.filter(item => item.language?.code === lng).sort((a, b) => a.price - b.price)
	const periods = Array.from(new Set(plans.map(item => item.months_number))).sort((a, b) => a - b)

	// States
	const [months, setMonths] = useState(periods[0] ?? 1)

	// Variables
	const visible = plans.filter(item => item.months_number === months)

	if (!plans.length) return <PricingEmptyMolecule lng={lng} title={empty.title} description={empty.description} action={locale.links.requestDemo} />

	return (
		<Box>
			{periods.length > 1 && (
				<Box sx={{ display: 'flex', justifyContent: 'center' }}>
					<PricingPeriodToggleMolecule
						label={period}
						value={months}
						onChange={setMonths}
						options={periods.map(value => ({
							label: locale.form.label.month[(value > 9 ? `${value}` : `0${value}`) as keyof typeof locale.form.label.month],
							value
						}))}
					/>
				</Box>
			)}

			{/* Prices, solutions and limits of these plans in one table */}
			<PricingComparisonOrganism lng={lng} locale={locale} plans={visible} />

			<Typography sx={{ mt: { xs: 4, md: 6 }, textAlign: 'center', fontSize: 15, lineHeight: 1.9, color: 'text.secondary' }}>
				{help.text}{' '}
				<Box component={Link} href={`/${lng}/signup`} sx={{ color: 'primary.dark', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 4 }}>
					{help.link}
				</Box>
			</Typography>
		</Box>
	)
}

export default PricingPlansOrganism
